import React, { useState } from 'react';
import { LineChart, ImageUp, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../components/ui/select';
import LegalDisclaimer from '../components/LegalDisclaimer';
import PriceAreaChart from '../components/premium-chart/PriceAreaChart';
import { chartAnalyzerAPI } from '../services/api';
import AnalysisResultCard from './chart-analyzer/AnalysisResultCard';
import UploadPanel from './chart-analyzer/UploadPanel';

const PERIODS = [
  { value: '1mo', label: '1 Month' },
  { value: '3mo', label: '3 Months' },
  { value: '6mo', label: '6 Months' },
  { value: '1y', label: '1 Year' },
  { value: '2y', label: '2 Years' },
];

export default function ChartAnalyzer() {
  const [symbol, setSymbol] = useState('');
  const [period, setPeriod] = useState('6mo');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const analyzeSymbol = async (e) => {
    e?.preventDefault();
    const s = symbol.trim().toUpperCase();
    if (!s) return;
    setLoading(true);
    setResult(null);
    try {
      const data = await chartAnalyzerAPI.analyzeSymbol(s, period);
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not analyze ${s}`);
    } finally {
      setLoading(false);
    }
  };

  const analyzeUpload = async (file, notes) => {
    setLoading(true);
    setResult(null);
    try {
      const data = await chartAnalyzerAPI.analyzeUpload(file, notes);
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not analyze the screenshot');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="chart-analyzer-page">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <LineChart className="text-purple-600" size={26} /> AI Chart Analyzer
          </h1>
          <p className="text-gray-500 mt-1">
            Search any stock or upload a chart screenshot — AI reads the trend, candlestick
            patterns, technicals and fundamentals, then suggests an educational trade idea with
            entry, take-profit and stop-loss.
          </p>
        </div>

        <Tabs defaultValue="symbol" onValueChange={() => setResult(null)}>
          <TabsList className="mb-4">
            <TabsTrigger value="symbol" data-testid="tab-symbol">
              <Sparkles size={14} className="mr-1.5" /> Search Symbol
            </TabsTrigger>
            <TabsTrigger value="upload" data-testid="tab-upload">
              <ImageUp size={14} className="mr-1.5" /> Upload Screenshot
            </TabsTrigger>
          </TabsList>

          <TabsContent value="symbol">
            <Card>
              <CardContent className="p-6">
                <form onSubmit={analyzeSymbol} className="flex flex-col sm:flex-row gap-3">
                  <Input
                    value={symbol}
                    onChange={(e) => setSymbol(e.target.value)}
                    placeholder="Ticker or company — e.g. AAPL, Tesla, Palantir"
                    className="flex-1"
                    data-testid="chart-symbol-input"
                  />
                  <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger className="w-full sm:w-36" data-testid="chart-period-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PERIODS.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="submit"
                    disabled={loading || !symbol.trim()}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="chart-symbol-analyze-btn"
                  >
                    {loading ? 'Analyzing…' : 'Analyze Chart'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="upload">
            <UploadPanel onAnalyze={analyzeUpload} loading={loading} />
          </TabsContent>
        </Tabs>

        {loading && (
          <Card className="mt-6" data-testid="chart-analyzer-loading">
            <CardContent className="p-8 text-center">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto" />
                <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto" />
                <div className="h-4 bg-gray-200 rounded w-1/3 mx-auto" />
              </div>
              <p className="text-sm text-gray-500 mt-4">
                AI is reading the chart — trend, patterns, levels… (~15s)
              </p>
            </CardContent>
          </Card>
        )}

        {result && (
          <div className="mt-6 space-y-6">
            {result.chart && (
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">
                    {result.symbol} · {result.period} daily
                  </p>
                  <PriceAreaChart data={result.chart.data} indicators={result.chart.indicators} />
                </CardContent>
              </Card>
            )}
            <AnalysisResultCard result={result} />
          </div>
        )}

        <div className="mt-10">
          <LegalDisclaimer variant="compact" />
        </div>
      </div>
    </div>
  );
}
